import * as THREE from 'three/webgpu';
import type { MeasureDxDyDzResult } from '../types/measure-property-types';
import {
    computeDeltaMillimeter,
    computeDistance3DMillimeter,
} from '../math/MeasureNumericKernel';

/**
 * 两点 ΔX / ΔY / ΔZ 分量差计算器。
 *
 * 职责：给定两个三维坐标点，计算第二点相对第一点的有符号坐标分量差及直线距离。
 * 语义对齐 SolidWorks 测量工具中的 Delta X / Delta Y / Delta Z 与 Distance（第二点 − 第一点）。
 * 输出单位：毫米，精度 4 位小数。
 */
export class MeasureDxDyDzCalculator {
    /**
     * 计算两点之间的 dx、dy、dz 及直线距离。
     *
     * @param from 第一点（基准点，场景米制）。
     * @param to 第二点（目标点，场景米制）。
     * @returns 分量差与距离（毫米）；分量与距离均按测量精度圆整。
     */
    public calculate(from: THREE.Vector3, to: THREE.Vector3): MeasureDxDyDzResult {
        // 第二点相对第一点的有符号差，与 SolidWorks Delta 口径一致
        const dx = computeDeltaMillimeter(to.x, from.x);
        const dy = computeDeltaMillimeter(to.y, from.y);
        const dz = computeDeltaMillimeter(to.z, from.z);
        // 距离基于场景米制坐标在 Big.js 中独立计算，避免分量圆整导致距离偏差
        const distance = computeDistance3DMillimeter(
            from.x,
            from.y,
            from.z,
            to.x,
            to.y,
            to.z,
        );

        return {
            dx,
            dy,
            dz,
            distance,
        };
    }
}
